import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { insightsApi, aiRecommendationsApi } from '../../lib/api';
import AINarrativePanel from '../../components/AINarrativePanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  TrendingUp, DollarSign, AlertTriangle,
  CheckCircle, ArrowRight, Heart, Lightbulb,
  Target, Clock, RefreshCw, Package, ClipboardList, ShieldAlert, XCircle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const COLORS = ['#0E7490', '#166534', '#92400E', '#991B1B', '#1A56DB', '#7C3AED'];

export default function ExecutiveInsightsPage() {
  const [data, setData] = useState(null);
  const [ops, setOps] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [insightsRes, opsRes, recRes] = await Promise.all([
        insightsApi.getExecutive().catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/insights/operations?days=30`, { headers: authHeader() }).catch(() => ({ data: null })),
        aiRecommendationsApi.get('executive').catch(() => ({ data: { recommendations: [] } }))
      ]);
      setData(insightsRes.data);
      setOps(opsRes.data);
      setRecs(recRes.data?.recommendations || []);
    } catch (err) {
      console.error('Failed to load insights:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;
  if (!data && !ops) return <div className="text-center py-8 text-[#64748B]">Failed to load insights</div>;

  const s = data?.summary || {};
  const claimsPieData = data ? Object.entries(data.claims_breakdown).filter(([,v]) => v > 0).map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v })) : [];

  return (
    <div className="space-y-6" data-testid="executive-insights-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Executive Insights
          </h1>
          <p className="text-[#64748B] mt-1">Data-driven decisions for hospital leadership</p>
        </div>
        <Button variant="outline" onClick={loadData} data-testid="refresh-insights">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <AINarrativePanel scope="executive_insights" contextData={data} />

      {/* Operations Insights — assets, cancellations, late tasks, aide incidents */}
      {ops && <OpsInsightsBlock ops={ops} />}

      {/* KPI Strip */}
      {data && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-card border-l-4 border-l-[#0E7490]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><DollarSign className="w-3 h-3" /> Total Billed</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">${s.total_billed?.toLocaleString() || '0'}</div>
            <div className="text-xs text-[#0E7490] mt-1">{s.collection_rate}% collection rate</div>
          </CardContent>
        </Card>
        <Card className="careiq-card border-l-4 border-l-[#166534]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><Heart className="w-3 h-3" /> Active Patients</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{s.total_patients}</div>
            <div className="text-xs text-[#991B1B] mt-1">{s.high_risk_patients} high-risk</div>
          </CardContent>
        </Card>
        <Card className={`careiq-card border-l-4 ${s.denial_rate > 15 ? 'border-l-[#991B1B]' : 'border-l-[#92400E]'}`}>
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Denial Rate</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{s.denial_rate}%</div>
            <div className="text-xs text-[#64748B] mt-1">Industry avg: 10%</div>
          </CardContent>
        </Card>
        <Card className={`careiq-card border-l-4 ${s.no_show_rate > 15 ? 'border-l-[#991B1B]' : 'border-l-[#166534]'}`}>
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><Clock className="w-3 h-3" /> No-Show Rate</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{s.no_show_rate}%</div>
            <div className="text-xs text-[#64748B] mt-1">Industry avg: 12%</div>
          </CardContent>
        </Card>
      </div>)}

      {/* Actionable Insights */}
      {data && (
      <Card className="careiq-card" data-testid="insights-cards">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-[#92400E]" /> Actionable Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-4">
            {data.insights.map((insight, i) => (
              <div key={i} className={`p-4 rounded-xl border-l-4 ${
                insight.type === 'warning' ? 'border-l-[#92400E] bg-[#92400E]/5' :
                insight.type === 'success' ? 'border-l-[#166534] bg-[#166534]/5' :
                'border-l-[#0E7490] bg-[#0E7490]/5'
              }`} data-testid={`insight-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    insight.type === 'warning' ? 'bg-[#92400E]/10' :
                    insight.type === 'success' ? 'bg-[#166534]/10' : 'bg-[#0E7490]/10'
                  }`}>
                    {insight.type === 'warning' ? <AlertTriangle className="w-4 h-4 text-[#92400E]" /> :
                     insight.type === 'success' ? <CheckCircle className="w-4 h-4 text-[#166534]" /> :
                     <Target className="w-4 h-4 text-[#0E7490]" />}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] text-[#64748B] uppercase tracking-wider">{insight.category}</div>
                    <div className="font-medium text-sm text-[#0A2342] mt-0.5">{insight.title}</div>
                    <p className="text-xs text-[#64748B] mt-1">{insight.description}</p>
                    <div className="text-xs font-medium mt-2 text-[#0A2342]">{insight.impact}</div>
                    <div className="flex items-center gap-1 mt-1 text-xs text-[#0E7490]">
                      <ArrowRight className="w-3 h-3" /> {insight.action}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>)}

      {/* Charts */}
      {data && (
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Revenue Trend (6 Months)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                <Area type="monotone" dataKey="revenue" stroke="#0E7490" fill="#0E7490" fillOpacity={0.1} strokeWidth={2} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="careiq-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Claims Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={claimsPieData} cx="50%" cy="50%" outerRadius={90} innerRadius={50} dataKey="value" nameKey="name" paddingAngle={2}>
                  {claimsPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>)}

      {/* Claims vs Denials Trend */}
      {data && (
      <Card className="careiq-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Claims vs Denials Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.monthly_trend} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip />
              <Bar dataKey="claims" name="Total Claims" fill="#0E7490" radius={[4, 4, 0, 0]} />
              <Bar dataKey="denials" name="Denials" fill="#991B1B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>)}

      {/* AI-Powered Recommendations */}
      {recs.length > 0 && (
        <Card className="careiq-card" data-testid="exec-ai-recommendations">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#92400E]" /> AI-Powered Action Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {recs.map((rec, i) => (
                <div key={i} className={`p-4 rounded-xl border-l-4 ${
                  rec.priority === 'high' || rec.priority === 'critical' ? 'border-l-[#991B1B] bg-[#991B1B]/5' :
                  rec.priority === 'medium' ? 'border-l-[#92400E] bg-[#92400E]/5' :
                  'border-l-[#0E7490] bg-[#0E7490]/5'
                }`} data-testid={`exec-rec-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${
                      rec.priority === 'high' || rec.priority === 'critical' ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                      rec.priority === 'medium' ? 'bg-[#92400E]/10 text-[#92400E]' : 'bg-[#0E7490]/10 text-[#0E7490]'
                    }`}>{rec.priority}</span>
                    <span className="text-[10px] text-[#64748B]">{rec.category}</span>
                  </div>
                  <div className="font-medium text-sm text-[#0A2342]">{rec.title}</div>
                  <p className="text-xs text-[#64748B] mt-1">{rec.description}</p>
                  {rec.actions && (
                    <div className="mt-2 space-y-1">
                      {rec.actions.slice(0, 2).map((a, j) => (
                        <div key={j} className="flex items-center gap-1 text-xs text-[#0E7490]">
                          <ArrowRight className="w-3 h-3 flex-shrink-0" /> {a}
                        </div>
                      ))}
                    </div>
                  )}
                  {rec.expected_impact && <div className="text-xs font-medium text-[#166534] mt-2">{rec.expected_impact}</div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------- Operations Insights Block ----------

function OpsInsightsBlock({ ops }) {
  const c = ops.cancellations || { total: 0, top_reasons: [] };
  const t = ops.overdue_tasks || { total: 0, by_user: [] };
  const a = ops.assets || { total: 0, by_status: {}, warranty_alerts: 0 };
  const ai = ops.aide_incidents || { top: [], bottom: [], all: [] };
  const tk = ops.tickets || { open: 0, breached: 0 };
  const opsInsights = ops.insights || [];
  return (
    <div className="space-y-4" data-testid="ops-insights-block">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="careiq-card border-l-4 border-l-[#7C3AED]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><Package className="w-3 h-3" /> Total Assets</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{a.total}</div>
            <div className="text-xs text-[#92400E] mt-1">{a.warranty_alerts} warranty alerts</div>
          </CardContent>
        </Card>
        <Card className="careiq-card border-l-4 border-l-[#991B1B]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancellations (30d)</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{c.total}</div>
            <div className="text-xs text-[#64748B] mt-1 truncate">{c.top_reasons[0]?.reason || 'No reasons logged'}</div>
          </CardContent>
        </Card>
        <Card className="careiq-card border-l-4 border-l-[#92400E]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Overdue Tasks</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{t.total}</div>
            <div className="text-xs text-[#64748B] mt-1 truncate">{t.by_user[0]?.name || '—'}</div>
          </CardContent>
        </Card>
        <Card className="careiq-card border-l-4 border-l-[#1A56DB]">
          <CardContent className="p-4">
            <div className="text-xs text-[#64748B] flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Open Tickets</div>
            <div className="text-2xl font-bold text-[#0A2342] mt-1">{tk.open}</div>
            <div className="text-xs text-[#991B1B] mt-1">{tk.breached} SLA breached</div>
          </CardContent>
        </Card>
      </div>

      {opsInsights.length > 0 && (
        <Card className="careiq-card" data-testid="ops-insights-cards">
          <CardHeader>
            <CardTitle className="text-base font-semibold text-[#0A2342] flex items-center gap-2">
              <Target className="w-4 h-4 text-[#0E7490]" /> Operations Storyline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-3">
              {opsInsights.map((it, i) => (
                <div key={i} className={`p-3 rounded-lg border-l-4 ${
                  it.type === 'warning' ? 'border-l-[#92400E] bg-[#92400E]/5' :
                  it.type === 'success' ? 'border-l-[#166534] bg-[#166534]/5' :
                  'border-l-[#0E7490] bg-[#0E7490]/5'
                }`} data-testid={`ops-insight-${i}`}>
                  <div className="text-[10px] text-[#64748B] uppercase tracking-wider">{it.category}</div>
                  <div className="font-medium text-sm text-[#0A2342] mt-0.5">{it.title}</div>
                  <p className="text-xs text-[#64748B] mt-1">{it.description}</p>
                  <div className="flex items-center gap-1 mt-1 text-xs text-[#0E7490]">
                    <ArrowRight className="w-3 h-3" /> {it.action}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-base font-semibold text-[#0A2342]">Top cancellation reasons</CardTitle></CardHeader>
          <CardContent>
            {c.top_reasons.length === 0 ? (
              <p className="text-xs text-[#64748B]">No cancellations in the last 30 days.</p>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={c.top_reasons} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" fontSize={11} />
                  <YAxis type="category" dataKey="reason" stroke="#64748B" fontSize={11} width={140} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#991B1B" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-base font-semibold text-[#0A2342]">Aide incident reporting (30d)</CardTitle></CardHeader>
          <CardContent>
            {ai.all.length === 0 ? (
              <p className="text-xs text-[#64748B]">No incidents reported by aides in the last 30 days.</p>
            ) : (
              <div className="space-y-2 text-sm" data-testid="aide-incident-list">
                <div className="text-[10px] uppercase tracking-wide text-[#64748B]">Most reports</div>
                {ai.top.map(r => (
                  <div key={r.user_id} className="flex items-center justify-between text-xs">
                    <span className="text-[#0A2342]">{r.name}</span>
                    <span className="font-mono text-[#991B1B]">{r.incidents}</span>
                  </div>
                ))}
                {ai.bottom.length > 0 && (
                  <>
                    <div className="text-[10px] uppercase tracking-wide text-[#64748B] pt-2 border-t border-[#E2E8F0] mt-2">Fewest reports</div>
                    {ai.bottom.map(r => (
                      <div key={r.user_id} className="flex items-center justify-between text-xs">
                        <span className="text-[#0A2342]">{r.name}</span>
                        <span className="font-mono text-[#166534]">{r.incidents}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {t.by_user.length > 0 && (
        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-base font-semibold text-[#0A2342]">Users with overdue tasks</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1 text-sm" data-testid="overdue-users-list">
              {t.by_user.slice(0, 10).map(u => (
                <div key={u.user_id || 'unassigned'} className="flex items-center justify-between border-b border-[#F1F5F9] py-1.5 text-xs">
                  <span className="text-[#0A2342]">{u.name}</span>
                  <span className="font-mono text-[#92400E]">{u.overdue_count} overdue</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}